/**
 * Projected-decal shader.
 *
 * Like the particles, decals are write-once: position, orientation, growth curve and
 * fade schedule are baked at spawn and evaluated from `uTime`, so a wall with 200
 * bullet holes costs zero CPU per frame. Blood pools spreading under a corpse are
 * just a growth curve on the instance.
 *
 * Decals are oriented quads, not cube-projected volumes: the level is built from
 * axis-aligned boxes, so a quad snapped to the hit normal is exact, one draw call,
 * and cannot bleed onto geometry behind the surface.
 */

export const decalVert = /* glsl */ `
attribute vec3 dPos;      // surface point (already offset along the normal)
attribute vec4 dQuat;     // orientation: local +Z is the surface normal
attribute vec4 dParams;   // size, birth, lifetime, fade-out duration
attribute vec4 dCol;      // rgb tint, atlas cell index
attribute vec4 dExtra;    // grow duration, size scale at birth, gloss, unused

uniform float uTime;
uniform vec2  uAtlasGrid;

varying vec2 vUv;
varying vec4 vColor;
varying vec3 vNormalW;
varying vec3 vTanW;
varying vec3 vBitW;
varying vec3 vWorld;
varying float vGloss;

vec3 qrot(vec4 q, vec3 v) {
  return v + 2.0 * cross(q.xyz, cross(q.xyz, v) + q.w * v);
}

void main() {
  float t = uTime - dParams.y;
  float life = dParams.z;
  if (t < 0.0 || t >= life) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  // Growth: blood pools creep outward over seconds; everything else snaps in.
  float grow = dExtra.x > 0.0 ? clamp(t / dExtra.x, 0.0, 1.0) : 1.0;
  grow = 1.0 - pow(1.0 - grow, 2.0);
  float size = dParams.x * mix(dExtra.y, 1.0, grow);

  vec3 local = vec3(position.xy * size, 0.0);
  vec3 world = dPos + qrot(dQuat, local);

  vNormalW = qrot(dQuat, vec3(0.0, 0.0, 1.0));
  vTanW    = qrot(dQuat, vec3(1.0, 0.0, 0.0));
  vBitW    = qrot(dQuat, vec3(0.0, 1.0, 0.0));
  vWorld   = world;
  vGloss   = dExtra.z;

  float idx = floor(dCol.w + 0.5);
  float col = mod(idx, uAtlasGrid.x);
  float row = floor(idx / uAtlasGrid.x);
  vUv = (uv + vec2(col, row)) / uAtlasGrid;

  // Fade in fast, fade out over the tail of the lifetime. Decals must never pop:
  // the pool schedules a fade on the slot it is about to recycle.
  float fadeIn  = smoothstep(0.0, 0.06, t);
  float fadeOut = 1.0 - smoothstep(life - max(dParams.w, 0.05), life, t);
  vColor = vec4(dCol.rgb, fadeIn * fadeOut);

  gl_Position = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
}
`;

export const decalFrag = /* glsl */ `
uniform sampler2D uAlbedo;
uniform sampler2D uNormal;
uniform vec3 uSunDir;      // world space, pointing *towards* the sun
uniform vec3 uSunColor;
uniform vec3 uAmbient;
uniform vec3 uCamPos;

varying vec2 vUv;
varying vec4 vColor;
varying vec3 vNormalW;
varying vec3 vTanW;
varying vec3 vBitW;
varying vec3 vWorld;
varying float vGloss;

void main() {
  vec4 tex = texture2D(uAlbedo, vUv);
  float a = tex.a * vColor.a;
  if (a < 0.006) discard;

  // Tangent-space crater normal -> world. The rim highlight on a bullet hole is
  // what makes it read as a hole rather than a sticker.
  vec3 nt = texture2D(uNormal, vUv).xyz * 2.0 - 1.0;
  vec3 n = normalize(vTanW * nt.x + vBitW * nt.y + vNormalW * nt.z);

  float ndl = max(dot(n, uSunDir), 0.0);
  vec3 albedo = tex.rgb * vColor.rgb;
  vec3 col = albedo * (uAmbient + uSunColor * ndl);

  // Wet surfaces (blood, water) keep a tight specular lobe.
  if (vGloss > 0.01) {
    vec3 v = normalize(uCamPos - vWorld);
    vec3 h = normalize(v + uSunDir);
    float spec = pow(max(dot(n, h), 0.0), mix(12.0, 90.0, vGloss)) * vGloss;
    col += uSunColor * spec * 1.6;
  }

  gl_FragColor = vec4(col * a, a);
}
`;
